import { ComboboxKeyboardEvent } from '../components';

/**
 * /**
 * Os eventos de teclado do select estão definidos pelo W3C WAI
 * https://www.w3.org/WAI/ARIA/apg/patterns/combobox/#keyboard_interaction
 *
 * [Tab]: The combobox is in the page Tab sequence.
 * Note: The popup indicator icon or button (if present), the popup, and the popup
 * descendants are excluded from the page Tab sequence.
 *
 * @param event evento de teclado
 * @param obj
 * @param obj.open se o foco está no combobox
 * @param obj.hasSelectedValue se o listbox tem um valor selecionado
 * @param obj.editable se o combobox é do tipo autocomplete
 * @param obj.inOnTheFirstOption se o foco está na primeira opção do listbox
 * @returns tipo de evento
 */
export const getComboboxActionByKeyboardEvent = (
  event: KeyboardEvent,
  {
    open = false,
    hasSelectedValue = false,
    editable = false,
    inOnTheFirstOption = false,
    hasValue = false,
  } = {},
): ComboboxKeyboardEvent => {
  const isEnterKey = event.code === 'Enter' || event.code === 'NumpadEnter';
  const isPrintableKey = event.key.length === 1;

  // [Backspace] (Optional): If the combobox is editable, returns focus to the
  // combobox and deletes the character prior to the cursor.
  if (event.code === 'Backspace' && editable && hasValue) {
    return 'erase';
  }

  // [Alt + Up Arrow] (Optional): If the popup is displayed:
  // - If the popup contains focus, returns focus to the combobox.
  // - Closes the popup.
  if (event.altKey && event.code === 'ArrowUp') {
    return 'close';
  }

  // [Alt + Down Arrow] (Optional): If the popup is available but not displayed,
  // displays the popup without moving focus.
  if (event.altKey && event.code === 'ArrowDown') {
    return 'open';
  }

  // [Escape]: Closes the popup and returns focus to the combobox.
  if (event.code === 'Escape') {
    // Optionally, if is editable and the popup is hidden before Escape is pressed, clears the combobox.
    if (!open && editable) {
      return 'clear';
    }

    // [Escape]: Dismisses the popup if it is visible.
    return 'close';
  }

  // [Up Arrow]: Moves focus to and selects the previous option. If focus is on the first option,
  // either returns focus to the combobox or does nothing.
  if (event.code === 'ArrowUp') {
    if (inOnTheFirstOption) {
      return 'close';
    }

    if (open) {
      // [Up Arrow] (Optional): If the popup is available, places focus on the
      // last focusable element in the popup.
      if (!hasSelectedValue) {
        return 'last';
      }

      return 'previous';
    }

    return 'none';
  }

  // [Down Arrow]: If the popup is available, moves focus into the popup
  // If the autocomplete behavior automatically selected a suggestion before
  // Down Arrow was pressed, focus is placed on the suggestion following the
  // automatically selected suggestion.
  if (event.code === 'ArrowDown') {
    // Otherwise, places focus on the first focusable element in the popup.
    if (!hasSelectedValue) {
      return 'first';
    }

    // [Down Arrow]: Moves focus to and selects the next option. If focus is on the last option, either
    // returns focus to the combobox or does nothing.
    return 'next';
  }

  // [Enter]: If the combobox is editable and an autocomplete suggestion is
  // selected in the popup, accepts the suggestion either by placing the input
  // cursor at the end of the accepted value in the combobox or by performing
  // a default action on the value. For example, in a messaging application,
  // the default action may be to add the accepted value to a list of message
  // recipients and then clear the combobox so the user can add another recipient.
  if (isEnterKey) {
    // [Enter]: Accepts the focused option in the listbox by closing the popup, placing the accepted
    // value in the combobox, and if the combobox is editable, placing the input cursor at the end of the value.
    return 'select';
  }

  if (event.code === 'Tab') {
    if (hasSelectedValue || hasValue) {
      return 'select';
    }

    return 'out';
  }

  // [Any printable character]:
  // - If the combobox is editable, returns the focus to the combobox without
  //   closing the popup and types the character.
  // - Otherwise, moves focus to the next option with a name that starts with
  //   the characters typed.
  if (isPrintableKey) {
    if (editable) {
      return 'type';
    }

    return 'search';
  }

  // [Right Arrow]: If the combobox is editable, returns focus to the combobox without closing the
  // popup and moves the input cursor one character to the right. If the input cursor is on the
  // right-most character, the cursor does not move.
  if (event.code === 'ArrowRight' && editable) {
    return 'cursor-to-right';
  }

  // [Left Arrow]: If the combobox is editable, returns focus to the combobox without closing the
  // popup and moves the input cursor one character to the left. If the input cursor is on the
  // left-most character, the cursor does not move.
  if (event.code === 'ArrowLeft' && editable) {
    return 'cursor-to-left';
  }

  // [Home] (Optional): Either moves focus to and selects the first option or, if the combobox is
  // editable, returns focus to the combobox and places the cursor on the first character.
  if (event.code === 'Home') {
    if (editable) {
      return 'cursor-to-start';
    }

    return 'first';
  }

  // [End] (Optional): Either moves focus to the last option or, if the combobox is editable,
  // returns focus to the combobox and places the cursor after the last character.
  if (event.code === 'End') {
    if (editable) {
      return 'cursor-to-end';
    }

    return 'last';
  }

  // [Delete] (Optional): If the combobox is editable, returns focus to the
  // combobox, removes the selected state if a suggestion was selected, and
  // removes the inline autocomplete string if present.
  if (event.code === 'Delete' && editable) {
    return 'clear';
  }

  return 'none';
};
